import type { ScreenContext } from "../../shell/types";
import { api, el, toast } from "../../ui/dom";
import { icon } from "../../ui/icons";
import { openModal } from "../../ui/modal";
import { card, chip, ib, table } from "../../ui/record";
import { area, field, pills, refuse } from "../clients/forms";
import { pageHead } from "../empty";

// FlowCare Platform › Tenants (brief 1E-6, mockup v59 platform/tenants and its Test companies card),
// for FlowCare staff only; the server answers "not available" to anyone else. The Test companies
// card (Test company, Has, Tests here, Enter as its admin, Reset sample data, New test company) and
// every company using FlowCare (Tenant, Type, Modules, Status, Last active, and the eye: enter it
// as its admin). A real company asks why before it's entered; both histories keep it.

interface TenantRow {
  id: string;
  name: string;
  type: string;
  modules: { key: string; name: string }[];
  status: string;
  test: boolean;
  lastActive: string;
  inside: boolean;
}
interface TestRow {
  id: string;
  name: string;
  has: string;
  testsHere: string;
}
interface TenantsBody {
  tenants: TenantRow[];
  testCompanies: TestRow[];
  modules: { key: string; name: string }[];
  inside: string | null;
}

/** The mockup's module icons (WSICON). */
const MODULE_ICON: Record<string, string> = {
  homecare: "heart",
  staffing: "briefcase",
  transport: "location",
  jobs: "megaphone",
};

export async function tenantsScreen(host: HTMLElement, ctx: ScreenContext) {
  host.append(
    pageHead(ctx, "Every company using FlowCare, its modules and its status.", [], "Platform"),
  );
  const body = el("div", { class: "wrap", style: "gap:18px" });
  host.append(body);
  let t: TenantsBody;

  async function load() {
    const r = await api<TenantsBody>("GET", "/api/platform/tenants");
    if (!r.ok) {
      body.replaceChildren(
        el("p", { class: "note", role: "alert" }, "Companies couldn't be loaded."),
      );
      return;
    }
    t = r.body;
    draw();
  }

  async function enter(row: { id: string; name: string; test: boolean }) {
    if (row.test) return go(row, null);
    // A real company: say why first. It goes in the company's own history and the platform log.
    const why = area("Reason for entering", "");
    const btn = el(
      "button",
      { type: "button", class: "btn pri" },
      icon("eye"),
      " Enter as its admin",
    );
    const m = openModal({
      title: `Enter ${row.name}`,
      sub: "Opens with admin access. Each change shows in its history as made from FlowCare.",
      body: [el("div", { class: "form" }, why.el)],
      actions: [btn],
    });
    btn.addEventListener("click", async () => {
      if (!why.input.value.trim())
        return refuse(m.note, "Give a reason for entering a real company.");
      btn.disabled = true;
      const ok = await go(row, why.input.value);
      btn.disabled = false;
      if (ok) m.close();
      else refuse(m.note, "That company couldn't be entered.");
    });
  }

  async function go(row: { id: string; name: string }, reason: string | null): Promise<boolean> {
    const r = await api("POST", `/api/platform/tenants/${row.id}/enter`, reason ? { reason } : {});
    if (!r.ok) {
      toast("That company couldn't be entered", r.body.error);
      return false;
    }
    toast(`Viewing ${row.name}`, "Exit from the bar at the top");
    location.assign("/");
    return true;
  }

  async function reset(row: TestRow, btn: HTMLButtonElement) {
    btn.disabled = true;
    const r = await api<{ archived: number }>("POST", `/api/platform/tenants/${row.id}/reset`, {});
    btn.disabled = false;
    if (!r.ok) return toast("Sample records weren't reset", r.body.error);
    toast(`Sample records reset for ${row.name}`, "Fresh sample records are in");
  }

  function newTest() {
    const name = field("Name", "Test · ", { maxlength: "80" });
    const mods = pills(
      "Modules to switch on",
      "checkbox",
      t.modules.map((m) => [m.key, m.name] as const),
      [],
      "Sample records fit that mix, and nothing else shows inside it.",
    );
    const make = el("button", { type: "button", class: "btn pri" }, icon("plus"), " Make it");
    const m = openModal({
      title: "New test company",
      sub: "Fictional sample records only.",
      body: [el("div", { class: "form" }, name.el, mods.el)],
      actions: [make],
    });
    make.addEventListener("click", async () => {
      make.disabled = true;
      const r = await api<{ name: string }>("POST", "/api/platform/test-companies", {
        name: name.input.value,
        modules: mods.values(),
      });
      make.disabled = false;
      if (!r.ok) return refuse(m.note, r.body.error);
      m.close();
      toast(`${r.body.name} is ready`, "With fictional sample records that fit its modules");
      await load();
    });
  }

  function draw() {
    const add = ib("plus", "New test company: pick its modules", newTest);
    const tests = card(
      el("span", {}, icon("checklist"), " Test companies"),
      "One per package, with fictional sample records. Test inside one to see what that customer sees.",
      [add],
      t.testCompanies.length
        ? table<TestRow>(
            [
              { h: "Test company", f: (r) => el("b", {}, r.name) },
              { h: "Has", f: (r) => r.has },
              { h: "Tests here", f: (r) => el("span", { class: "small" }, r.testsHere) },
              {
                h: "",
                f: (r) => {
                  const resetBtn = ib(
                    "sync",
                    `Reset sample records for ${r.name}`,
                    () => void reset(r, resetBtn),
                  );
                  return el(
                    "span",
                    { class: "actions", "data-test-company": r.name },
                    ib(
                      "eye",
                      `Enter ${r.name} as its admin`,
                      () => void enter({ id: r.id, name: r.name, test: true }),
                    ),
                    resetBtn,
                  );
                },
              },
            ],
            t.testCompanies,
            undefined,
            "Test companies",
          )
        : el("p", { class: "muted" }, "No test companies yet. Make one per package with the plus."),
    );
    const list = table<TenantRow>(
      [
        {
          h: "Company",
          f: (r) =>
            el(
              "span",
              {},
              el("b", {}, r.name),
              r.test ? el("span", { class: "muted small" }, " (sample)") : "",
            ),
        },
        { h: "Type", f: (r) => r.type },
        {
          h: "Modules",
          f: (r) =>
            el(
              "span",
              { class: "modicons" },
              ...r.modules.map((m) =>
                el(
                  "span",
                  { "data-tip": m.name, "aria-label": m.name, role: "img" },
                  icon(MODULE_ICON[m.key] ?? "apps"),
                ),
              ),
            ),
        },
        {
          h: "Status",
          f: (r) =>
            chip(r.status, r.status.startsWith("Active") ? (r.test ? "info" : "ok") : "warn"),
        },
        { h: "Last active", f: (r) => r.lastActive },
        {
          h: "",
          f: (r) =>
            r.inside
              ? chip("Entered", "info")
              : ib("eye", `Enter ${r.name} as its admin`, () => void enter(r)),
        },
      ],
      t.tenants,
      undefined,
      "Companies",
    );
    body.replaceChildren(tests, el("section", { "aria-label": "Companies" }, list));
  }

  await load();
}
